import axios from "axios";
import { collectorAuthHeaders } from "./collectorSession";
import { withCollectorSession } from "../services/collectorApi";
import { isMediaOptimizationEnabled } from "../media/mediaApi";
import { CollectorMediaError, uploadCollectorMediaPhoto } from "../media/collectorMediaUpload";

const base = process.env.REACT_APP_SERVERURL || "";

export const ADDITIONAL_IMAGE_CAP = 10;
export const REGULATORY_DOC_CAP = 10;
export const COLLECTOR_IMAGE_MAX_BYTES = 8 * 1024 * 1024;
export const COLLECTOR_DOC_MAX_BYTES = 10 * 1024 * 1024;

/**
 * A piece photo. With the media service on (UAT and prod) it is stored privately as "hkp-media:<id>" with sized
 * copies and an archived original; the legacy public upload is only the fallback for builds without it, or for
 * an older backend that has no /collector/media (404 or 501 on init). Any other refusal (sign-in, quota, size)
 * is shown to the person, never routed around.
 */
export async function uploadCollectorPhoto(
  file: File,
  onProgress?: (percent: number) => void
): Promise<string> {
  if (!isMediaOptimizationEnabled()) return uploadCollectorFile(file, onProgress);
  try {
    return await uploadCollectorMediaPhoto(file, onProgress);
  } catch (error) {
    const missing = error instanceof CollectorMediaError && error.step === "init"
      && (error.status === 404 || error.status === 501);
    if (!missing) throw error;
    return uploadCollectorFile(file, onProgress);
  }
}

/** Legacy upload, still used for documents (PDFs) and builds without the media service. */
export async function uploadCollectorFile(
  file: File,
  onProgress?: (percent: number) => void
): Promise<string> {
  onProgress?.(0);
  // The server only signs uploads for a signed-in collector; an expired session renews once and retries.
  const presignedRes = await withCollectorSession(
    () => axios.post(`${base}/upload/productPresignedURL`, { fileType: file.type }, { headers: collectorAuthHeaders() }),
    (error: any) => error?.response?.status === 401
  );
  const presignedUrl: string = presignedRes.data?.data;
  if (!presignedUrl) {
    throw new Error("Could not get upload URL");
  }
  const uploadUrl = presignedUrl.includes("x-amz-acl")
    ? presignedUrl
    : `${presignedUrl}&x-amz-acl=public-read`;
  await axios.put(uploadUrl, file, {
    headers: { "Content-Type": file.type },
    onUploadProgress: (evt) => {
      const total = evt.total || file.size || 0;
      if (!total) return;
      onProgress?.(Math.min(100, Math.round((evt.loaded * 100) / total)));
    },
  });
  onProgress?.(100);
  return presignedUrl.split("?")[0];
}

export function assertCollectorImage(file: File): void {
  if (!file.type.startsWith("image/")) {
    throw new Error("Please choose a photo.");
  }
  if (file.size > COLLECTOR_IMAGE_MAX_BYTES) {
    throw new Error("Photos must be under 8 MB.");
  }
}

export function assertCollectorDoc(file: File): void {
  const name = file.name.toLowerCase();
  const allowedType = /^(application\/pdf|image\/(jpeg|jpg|png|webp))$/.test(file.type);
  const allowedName = /\.(pdf|jpe?g|png|webp)$/.test(name);
  if (!allowedType && !allowedName) {
    throw new Error("Use a PDF or an image of the bill or certificate.");
  }
  if (file.size > COLLECTOR_DOC_MAX_BYTES) {
    throw new Error("Documents must be under 10 MB.");
  }
}

export function parsePurchasePrice(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const value = Number(trimmed.replace(/,/g, ""));
  if (!Number.isFinite(value) || value < 0) {
    throw new Error("Enter a valid price, or leave it blank.");
  }
  return value;
}

export const PURCHASE_CURRENCIES = [
  { code: "INR", label: "INR" },
  { code: "USD", label: "USD" },
  { code: "EUR", label: "EUR" },
  { code: "GBP", label: "GBP" },
  { code: "AED", label: "AED" },
  { code: "SGD", label: "SGD" },
  { code: "AUD", label: "AUD" },
  { code: "CAD", label: "CAD" },
  { code: "JPY", label: "JPY" },
  { code: "CHF", label: "CHF" },
  { code: "SAR", label: "SAR" },
  { code: "THB", label: "THB" },
] as const;

export const PUBLIC_DISPLAY_FIELD_OPTIONS = [
  { key: "itemCategory", label: "Category" },
  { key: "craftType", label: "Craft type" },
  { key: "color", label: "Color" },
  { key: "fabric", label: "Fabric" },
  { key: "clusterType", label: "Region / cluster" },
  { key: "occasion", label: "Occasion" },
  { key: "makerName", label: "Company / brand / artisan" },
] as const;
